/**
 * Blackthorn's Storm event core: Lord Blackthorn's theatre + the pure, seeded,
 * clock-free **timeline** of the whole event (PRD #289, Slice D, ADR-0151).
 *
 * This is the storm's sibling to {@link ./ratSwarm}: where the rat swarm owns *its*
 * decisions (where rats spawn, when they skitter, whether a stomp kills), this owns
 * the storm's **choreography** - where Lord Blackthorn appears, when he casts and
 * teleports, which telegraphed spot the next vortex materializes on, when the whole
 * pageant ends - as a tick-driven state machine that reads no wall clock and draws
 * all placement randomness from the world's seeded `rng` (the `spawnRats` precedent).
 * The vortexes it summons are driven by the separate {@link ./vortex} pursuit brain;
 * this module only *spawns* them into being on the schedule and plays their birth and
 * death clips.
 *
 * The **acts** it cannot own - broadcasting the herald/taunt/telegraph/animation
 * cues, snapshotting the enrolled sessions, tracking who the vortexes downed, crediting
 * survivors a Storm Mark, resurrecting the fallen, and the deferred despawns - are
 * server I/O and live in `server/gameServer.ts` (which steps this once per tick and
 * enacts the {@link StormBeat}s it returns). So, exactly like the swarm, nothing here
 * touches a socket, a session, or an account: it returns beats and mutates only world
 * entities (spawning vortexes, moving Blackthorn).
 *
 * Event state ({@link StormState}) is ephemeral server memory beside the world, never
 * persisted and never on a snapshot - a storm is a non-deterministic operator/scheduler
 * action whose theatre should not survive a restart (the {@link RatSwarmState} model).
 */
import type { Entity, EntityId } from './entity';
import type { World } from './world';
import { Mobile } from './mobile';
import { ALL_DIRECTIONS, Direction } from './direction';
import {
  createVortex,
  createVortexPursuit,
  rollLlamaVariant,
  LLAMA_SPAWN_CHANCE,
  LLAMA_MS_PER_TILE,
  VORTEX_DAMAGE_MIN,
  VORTEX_DAMAGE_MAX,
  type VortexPursuitState,
} from './vortex';
import { BLACKTHORN_BODY } from './content';
import { BANK_ROOF_Z } from './teleporters';
import { walkableForLevel } from './walkability';
import { BANK_INTERIOR, type RatRegion } from './ratSwarm';

// ---------------------------------------------------------------------------
// Tunables. Every knob of the storm, in one place (PRD #289 starting values).
// All spans are expressed in **ticks** (the server's 50ms `STEP_MS` fixed steps,
// 20 ticks = 1s), never milliseconds, because the sim is clock-free: the server
// feeds `stepStorm` one call per tick and every cadence falls out of counting,
// exactly like `SWARM_DURATION_TICKS`.
// ---------------------------------------------------------------------------

/** Lead-in from Blackthorn's roof spawn (t-10s) to the first summon (t=0). */
export const STORM_HERALD_LEAD_TICKS = 200;

/**
 * The combat clock, measured from t=0 (the first summon). **1:30** (1800 ticks @ 50ms):
 * the original 1:00 (1200) extended by **30 seconds** (600 ticks) so the storm lasts
 * longer (ADR-0162). {@link STORM_END_TICKS} and the client countdown derive from this, so
 * the single bump lengthens the whole event; the scripted opening/phase-2 beats keep their
 * fixed absolute ticks and simply leave more fight after phase 2.
 */
export const STORM_COMBAT_TICKS = 1800;

/** Absolute event length from spawn to end: the herald lead plus the combat clock. */
export const STORM_END_TICKS = STORM_HERALD_LEAD_TICKS + STORM_COMBAT_TICKS;

/**
 * Absolute tick (from spawn) Blackthorn teleports down to the **south floor anchor**
 * and telegraphs the first ground pair (t+3s). The opening is a fixed three-stage
 * descent - roof, south, rear - each summon telegraphed, not the old single teleport
 * (PRD #289 storm fix: many more vortexes right out of the gate, all telegraphed).
 */
export const STORM_TELEPORT_TICKS = STORM_HERALD_LEAD_TICKS + 60;

/** Absolute tick (from spawn) Blackthorn teleports **behind the bank** and telegraphs the second ground pair. */
export const STORM_REAR_TELEPORT_TICKS = STORM_HERALD_LEAD_TICKS + 160;

/**
 * Absolute tick (from spawn) the **phase-2** wave materializes (t+30s into the 1:30
 * combat clock): Blackthorn teleports to the **front of the bank**
 * ({@link STORM_FRONT_ANCHOR}) and summons {@link STORM_PHASE2_COUNT} more vortexes at
 * seeded floor spots, each telegraphed {@link STORM_TELEGRAPH_LEAD_TICKS} early.
 */
export const STORM_PHASE2_TICKS = STORM_HERALD_LEAD_TICKS + 600;

/** How many extra vortexes the phase-2 wave summons. */
export const STORM_PHASE2_COUNT = 6;

/** How long before a summon its telegraph fires - ~2s. */
export const STORM_TELEGRAPH_LEAD_TICKS = 40;

/** Cadence of Blackthorn's taunt lines. */
export const STORM_TAUNT_INTERVAL_TICKS = 100;

/**
 * The fixed part of a storm's vortex host: the scripted opening (1 roof + 2 south + 2
 * rear = 5), summoned before the phase-2 wave whatever its size. The per-storm cap is
 * this plus the phase-2 count (see {@link stormMaxVortexes}).
 */
export const STORM_OPENING_VORTEXES = 5;

/**
 * Total vortexes a single storm summons: the scripted opening ({@link STORM_OPENING_VORTEXES})
 * plus the phase-2 wave ({@link STORM_PHASE2_COUNT}). Also the safety cap. This is the
 * default; when the phase-2 count is retuned at runtime the cap **derives automatically**
 * from it via {@link stormMaxVortexes} (never edited independently) so the two can never
 * disagree (ADR-0182).
 */
export const STORM_MAX_VORTEXES = STORM_OPENING_VORTEXES + STORM_PHASE2_COUNT;

// ---------------------------------------------------------------------------
// Runtime tuning. The storm engine reads these values from module scope by
// default (the constants above), but a caller may thread a `StormTuning` object
// into `startStorm` to override them for that storm - the admin storm-tuning
// surface (ADR-0182). The sim stays pure and seeded: the tuning is passed in and
// carried on `StormState`, never read from ambient/server state, so a seeded test
// is fully deterministic and the shipped defaults reproduce today's behaviour.
// ---------------------------------------------------------------------------

/**
 * The runtime-tunable knobs of a storm, in the sim's own units (ticks, ms-per-tile, a
 * 0..1 fraction), carried on {@link StormState} and read by {@link stepStorm} /
 * {@link stormTicksRemaining}. The admin surface edits human-friendly units (seconds,
 * a percent, a speed multiplier) and converts to this shape server-side before it reaches
 * the sim (ADR-0182). Keeping the current constants as {@link DEFAULT_STORM_TUNING} means
 * an absent tuning reproduces today's storm exactly.
 */
export interface StormTuning {
  /**
   * How many vortexes the phase-2 wave summons ({@link STORM_PHASE2_COUNT} default). The
   * per-storm cap ({@link stormMaxVortexes}) derives from this - the opening five plus this.
   */
  phase2Count: number;
  /** The combat clock length in ticks ({@link STORM_COMBAT_TICKS} default; the end derives from it). */
  combatTicks: number;
  /** Chance (0..1) each summon rolls a chaos-llama variant ({@link LLAMA_SPAWN_CHANCE} default). */
  llamaSpawnChance: number;
  /** A chaos-llama's gait in ms per tile ({@link LLAMA_MS_PER_TILE} default; lower = faster). */
  llamaMsPerTile: number;
  /** The inclusive normal-vortex bite damage range ({@link VORTEX_DAMAGE_MIN}/{@link VORTEX_DAMAGE_MAX} defaults). */
  damageMin: number;
  damageMax: number;
}

/** The shipped storm tuning: the module-scope constants, so an absent tuning is a no-op. */
export const DEFAULT_STORM_TUNING: StormTuning = {
  phase2Count: STORM_PHASE2_COUNT,
  combatTicks: STORM_COMBAT_TICKS,
  llamaSpawnChance: LLAMA_SPAWN_CHANCE,
  llamaMsPerTile: LLAMA_MS_PER_TILE,
  damageMin: VORTEX_DAMAGE_MIN,
  damageMax: VORTEX_DAMAGE_MAX,
};

/**
 * The per-storm vortex cap for a given tuning: the fixed opening
 * ({@link STORM_OPENING_VORTEXES}) plus the tunable phase-2 count. Derived, never stored
 * independently, so retuning the wave size keeps the cap consistent (ADR-0182).
 */
export function stormMaxVortexes(tuning: StormTuning = DEFAULT_STORM_TUNING): number {
  return STORM_OPENING_VORTEXES + tuning.phase2Count;
}

/** The absolute event length (spawn -> end) for a tuning: the herald lead plus its combat clock. */
export function stormEndTicks(tuning: StormTuning = DEFAULT_STORM_TUNING): number {
  return STORM_HERALD_LEAD_TICKS + tuning.combatTicks;
}

/** Blackthorn's roof spawn anchor (the bank roof-access plate, z {@link BANK_ROOF_Z}). */
export const STORM_ROOF_ANCHOR = { x: 83, y: 99 } as const;

/** The tile the opening roof vortex drops on (on the roof plate, z {@link BANK_ROOF_Z}). */
export const STORM_ROOF_VORTEX = { x: 91, y: 94 } as const;

/** Blackthorn's first ground teleport: south of the bank, where he summons the first pair. */
export const STORM_FLOOR_ANCHOR = { x: 90, y: 109 } as const;

/** Blackthorn's second ground teleport: behind the bank, where he summons the second pair. */
export const STORM_REAR_ANCHOR = { x: 94, y: 76 } as const;

/** Blackthorn's teleport back to the **front** of the bank, where he calls down phase 2. */
export const STORM_FRONT_ANCHOR = { x: 100, y: 106 } as const;

/** The first telegraphed ground pair, summoned together from the south floor anchor. */
export const STORM_FRONT_PAIR = [
  { x: 85, y: 104 },
  { x: 97, y: 105 },
] as const;

/** The second telegraphed ground pair, summoned together from behind the bank. */
export const STORM_REAR_PAIR = [
  { x: 86, y: 77 },
  { x: 97, y: 85 },
] as const;

/** The hue Blackthorn's taunt speech is tinted (a menacing purple; cosmetic). */
export const STORM_TAUNT_HUE = 33;

/** Lord Blackthorn's display name, stamped on his entity and his taunt lines. */
export const BLACKTHORN_NAME = 'Lord Blackthorn';

/** The server-wide herald line announced when Blackthorn first appears. */
export const STORM_START_TEXT = 'The sky darkens - Lord Blackthorn descends upon the bank!';

/** The server-wide herald line when the storm passes and the fallen are raised. */
export const STORM_END_TEXT = 'The storm breaks. Blackthorn withdraws, and the fallen stir once more.';

/** The herald line when a storm cancels an in-progress duel at t-10s. */
export const STORM_DUEL_CANCEL_TEXT = 'A gathering storm sunders every duel - blades are lowered.';

/** Blackthorn's taunt rotation, cycled deterministically by {@link StormState.tauntSeq}. */
export const STORM_TAUNTS: readonly string[] = [
  'You cower in your little bank while the realm burns!',
  'My vortexes will scour this vault of every last coward.',
  'Run, sitters - run! It changes nothing.',
  'Britannia belongs to me, and so do you.',
  'There is no safety here. There never was.',
];

/** Blackthorn's exit line, spoken over his own head as the storm ends. */
export const STORM_EXIT_TAUNT = 'Bah! Enjoy your reprieve. I shall return.';

/** Blackthorn's laugh over his own head when the bank is wiped early (0 standing). */
export const STORM_WIPE_TAUNT = 'Hahaha! Not one of you left standing. Pathetic!';

/** The server-wide herald when the storm redoubles at the phase-2 point (t+30s). */
export const STORM_PHASE2_TEXT = 'The storm redoubles - Blackthorn summons a fresh host of vortexes!';

/**
 * The server-wide **finale herald** when nobody fell - a flawless bank (Slice F,
 * ADR-0153). Broadcast after {@link STORM_END_TEXT} when every enrolled banksitter
 * stood the whole storm.
 */
export const STORM_FLAWLESS_TEXT =
  'Not one soul fell — the bank stood unbroken. Blackthorn departs thwarted.';

/**
 * The server-wide **finale herald** when nobody survived - Blackthorn gloats (Slice
 * F, ADR-0153). Broadcast when every enrolled banksitter who stayed was struck down.
 */
export const STORM_WIPE_TEXT =
  'None were left standing. Blackthorn laughs as he quits the emptied bank.';

/**
 * The outcome-specific **finale herald** the server broadcasts when a storm ends
 * (Slice F, PRD #289 / ADR-0153), on top of the generic {@link STORM_END_TEXT}: the
 * survivor count, or a flawless-bank / total-wipe callout at the extremes. Pure of
 * the world so it is unit-testable; the server supplies the tallies it computes in
 * `endStorm` (`survivors` = enrolled players still standing, `fallen` = casualties).
 *
 * Returns `null` when the storm ran over an empty bank (nobody enrolled, so nothing
 * to report) - the caller then broadcasts only the generic line. "Those who stood,
 * remember it" is the survivor voice; the singular is handled so a lone survivor
 * reads naturally.
 */
export function stormFinaleLine(survivors: number, fallen: number): string | null {
  const s = Math.max(0, Math.floor(survivors));
  const f = Math.max(0, Math.floor(fallen));
  if (s === 0 && f === 0) return null; // an empty bank: no toll to herald
  if (f === 0) return STORM_FLAWLESS_TEXT; // nobody fell
  if (s === 0) return STORM_WIPE_TEXT; // nobody survived - Blackthorn gloats
  return s === 1
    ? 'One banksitter stood against the storm and will remember it.'
    : `${s} banksitters stood against the storm and will remember it.`;
}

// ---------------------------------------------------------------------------
// Blackthorn the entity.
// ---------------------------------------------------------------------------

/**
 * Create Lord Blackthorn and register him: an ordinary named mobile entity carrying
 * his restricted-bake people-band body (Slice B) at the given tile and level, plus a
 * live {@link Mobile} so he can be teleported from roof to floor. He has **no health**
 * (unkillable, a spectacle not a fight) and, being session-less, is never a valid duel
 * or vortex target. The timeline ({@link stepStorm}) drives his casts and teleport; this
 * factory only places him.
 */
export function createBlackthorn(
  world: World,
  spec: { x: number; y: number; z: number; facing: Direction },
): Entity {
  return world.createEntity({
    position: { x: spec.x, y: spec.y, facing: spec.facing, z: spec.z },
    mobile: new Mobile({ x: spec.x, y: spec.y, facing: spec.facing }),
    renderable: { body: BLACKTHORN_BODY, name: BLACKTHORN_NAME },
    // The replicated interaction role (D5): a spectacle, never a target.
    interaction: { role: 'scenery' },
  });
}

// ---------------------------------------------------------------------------
// The event state.
// ---------------------------------------------------------------------------

/** How a storm is (or stopped) running. */
export type StormStatus = 'active' | 'ended';

/**
 * A telegraphed, still-pending summon wave: the ground tiles the vortexes will
 * materialize on (one telegraph each), and the absolute tick (from spawn) they all fire
 * together. Armed ~2s early so players can read the danger before the vortexes land.
 */
export interface StormSummonWave {
  spots: { x: number; y: number }[];
  fireAtTick: number;
}

/**
 * The whole storm's choreography state - ephemeral server memory beside the world (never
 * persisted, never snapshotted; see the module header). Mutated only by {@link stepStorm}
 * (the timeline) and the server (which populates {@link enrolled}/{@link casualties} from
 * its session bookkeeping, exactly as the swarm's server fills its tallies).
 */
export interface StormState {
  status: StormStatus;
  /**
   * The runtime tuning this storm runs under (ADR-0182), captured at {@link startStorm}
   * from the caller (the server's live admin storm-tuning) or {@link DEFAULT_STORM_TUNING}.
   * Read throughout the timeline so a mid-flight change never disturbs a running storm -
   * only the **next** storm picks up an edit.
   */
  tuning: StormTuning;
  /** Lord Blackthorn's entity id (spawned on the roof at {@link startStorm}). */
  blackthorn: EntityId;
  /** Ids of every energy vortex currently alive this storm (the vortex pursuit reads these). */
  vortexes: Set<EntityId>;
  /** The vortex pursuit brains (one per vortex), carried here so the server steps them each tick. */
  pursuit: VortexPursuitState;
  /** The floor region escalation vortexes may be telegraphed into. */
  region: RatRegion;
  /** Ticks elapsed since Blackthorn spawned (t-10s); the timeline's whole clock. */
  elapsedTicks: number;
  /** How many vortexes this storm has summoned so far (up to {@link STORM_MAX_VORTEXES}). */
  vortexCount: number;
  /** The armed-but-not-yet-fired telegraphed summon wave, or `null` when nothing is pending. */
  pendingSummon: StormSummonWave | null;
  /** Elapsed tick of Blackthorn's last taunt, for the taunt cadence. */
  lastTauntTick: number;
  /** Monotonic taunt counter, so the rotation is deterministic. */
  tauntSeq: number;
  /**
   * Opaque session keys snapshotted at t-10s (the server fills this): everyone present
   * when the storm began, the pool that can earn a Mark. Reserved keys are account names,
   * anonymous keys are session ids (the swarm-tally key model).
   */
  enrolled: Set<string>;
  /**
   * The storm's casualties, keyed by the same opaque session key, mapped to the display
   * name (used for the per-fall herald). The server adds a key when a vortex downs that
   * player, and the mass-resurrection at the end targets exactly this set.
   */
  casualties: Map<string, { name: string }>;
}

/**
 * The nearest walkable tile to `(ax, ay)` on level `z` within `maxRadius`, by a bounded
 * outward ring scan, or the anchor itself as a last resort. Placement helper so a spawn
 * anchor that is a hair off a walkable cell still lands the actor on real floor/roof.
 */
function nearestWalkable(
  isWalkable: (x: number, y: number) => boolean,
  ax: number,
  ay: number,
  maxRadius = 6,
): { x: number; y: number } {
  if (isWalkable(ax, ay)) return { x: ax, y: ay };
  for (let r = 1; r <= maxRadius; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
        const x = ax + dx;
        const y = ay + dy;
        if (isWalkable(x, y)) return { x, y };
      }
    }
  }
  return { x: ax, y: ay };
}

/**
 * A seeded walkable ground tile inside `region` for an escalation vortex's telegraphed
 * spot. Draws from `world.rng` (the sanctioned sim randomness that rides the snapshot,
 * the `spawnRats` precedent), falling back to the region's first walkable cell if the
 * random pick somehow finds nothing. Throws only if the region has no walkable tile at
 * all - a mis-authored region should fail loudly, never telegraph into a wall.
 */
function pickTelegraphSpot(world: World, region: RatRegion): { x: number; y: number } {
  const isWalkable = walkableForLevel(world, 0);
  const candidates: { x: number; y: number }[] = [];
  for (let y = region.y0; y < region.y1; y++) {
    for (let x = region.x0; x < region.x1; x++) {
      if (isWalkable(x, y)) candidates.push({ x, y });
    }
  }
  if (candidates.length === 0) throw new Error('pickTelegraphSpot: region has no walkable tile');
  return candidates[world.rng.nextInt(candidates.length)]!;
}

/** A seeded facing for a freshly-placed actor (the `spawnRats` facing draw). */
function seededFacing(world: World): Direction {
  return ALL_DIRECTIONS[world.rng.nextInt(ALL_DIRECTIONS.length)]!;
}

/**
 * Start a storm: spawn Lord Blackthorn on the bank roof and return the fresh event
 * state tracking the whole pageant. The single sim-side entrypoint the server's operator
 * trigger and auto-scheduler both call (Slice D); refusing a second concurrent event -
 * and mutual exclusion against a rat swarm - is the server's job, not the core's. The
 * enrolled-session snapshot is the server's to fill immediately after (it alone knows the
 * sessions); this returns it empty.
 *
 * `opts.tuning` overrides the storm's runtime knobs (wave size, duration, llama chance/speed,
 * bite damage) for this storm only (ADR-0182), captured onto {@link StormState.tuning};
 * absent, the shipped {@link DEFAULT_STORM_TUNING} reproduces today's storm exactly.
 */
export function startStorm(
  world: World,
  opts: { region?: RatRegion; tuning?: StormTuning } = {},
): StormState {
  const region = opts.region ?? BANK_INTERIOR;
  const tuning = opts.tuning ?? DEFAULT_STORM_TUNING;
  const roofWalkable = walkableForLevel(world, BANK_ROOF_Z);
  const roof = nearestWalkable(roofWalkable, STORM_ROOF_ANCHOR.x, STORM_ROOF_ANCHOR.y);
  const blackthorn = createBlackthorn(world, {
    x: roof.x,
    y: roof.y,
    z: BANK_ROOF_Z,
    facing: Direction.S, // he looms over the bank he is about to scour, facing south
  });
  return {
    status: 'active',
    tuning,
    blackthorn: blackthorn.id,
    vortexes: new Set(),
    pursuit: createVortexPursuit(),
    region,
    elapsedTicks: 0,
    vortexCount: 0,
    pendingSummon: null,
    lastTauntTick: 0,
    tauntSeq: 0,
    enrolled: new Set(),
    casualties: new Map(),
  };
}

// ---------------------------------------------------------------------------
// The timeline stepper.
// ---------------------------------------------------------------------------

/**
 * One theatrical beat the server should enact this tick (Slice D/E). The world
 * mutations a beat implies - spawning the vortex, moving Blackthorn - have **already**
 * been applied by {@link stepStorm}; a beat is the broadcast cue, not a request. The
 * server maps each to an existing broadcast (a herald line, an overhead taunt, a one-shot
 * action animation, an ephemeral telegraph message).
 */
export type StormBeat =
  /** A server-wide herald line (the `/top`-style broadcast). */
  | { kind: 'herald'; text: string }
  /** A taunt spoken over Blackthorn's own head. */
  | { kind: 'taunt'; text: string }
  /** Blackthorn plays his summon-cast clip (like the stomp swing). */
  | { kind: 'cast' }
  /** Blackthorn has been repositioned (roof -> floor); a cue for Slice E. */
  | { kind: 'teleport' }
  /** A vortex will materialize on `(x, y)` in `ticksUntil` ticks; render the danger telegraph. */
  | { kind: 'telegraph'; x: number; y: number; ticksUntil: number }
  /** A vortex just materialized; play its birth (materialize) clip. */
  | { kind: 'vortex-spawn'; vortex: EntityId }
  /** A vortex is dissolving; play its death clip before the server despawns it. */
  | { kind: 'vortex-die'; vortex: EntityId };

/** What one {@link stepStorm} produced: the beats to enact, and whether the storm ended this tick. */
export interface StormStep {
  beats: StormBeat[];
  /** `true` on the single tick the event ends (mass-res + scoring + despawn are the server's to do). */
  ended: boolean;
}

/**
 * Teleport Blackthorn to a **ground** tile and emit the teleport beat: snap the target
 * to real floor, move the mobile, and drop his entity's `z` to 0 (the movement system
 * mirrors x/y from the mobile but **not** z, the roof-plate rule). A no-op on his sprite
 * if he has somehow lost his mobile/position.
 */
function teleportBlackthorn(
  world: World,
  state: StormState,
  to: { x: number; y: number },
  beats: StormBeat[],
): void {
  const blackthorn = world.get(state.blackthorn);
  if (blackthorn?.mobile && blackthorn.position) {
    const spot = nearestWalkable(walkableForLevel(world, 0), to.x, to.y);
    blackthorn.mobile.teleport(spot.x, spot.y);
    blackthorn.position.x = spot.x;
    blackthorn.position.y = spot.y;
    // Ground landing resolved through the terrain height (PRD #536 Slice B):
    // he stands ON a hill, not inside it. Flat maps: the old literal 0.
    blackthorn.position.z = world.map.groundedZ(spot.x, spot.y, 0);
  }
  beats.push({ kind: 'teleport' });
}

/**
 * Arm a telegraphed **ground** summon wave: snap each requested spot to real floor,
 * remember them to fire in {@link STORM_TELEGRAPH_LEAD_TICKS} ticks, and emit one
 * telegraph beat per spot so players can read the danger before the vortexes land. The
 * snapped spots are stored (not the raw requests) so the telegraph and the eventual
 * materialization can never disagree.
 */
function armSummonWave(
  world: World,
  state: StormState,
  e: number,
  spots: readonly { x: number; y: number }[],
  beats: StormBeat[],
): void {
  const isWalkable = walkableForLevel(world, 0);
  const snapped = spots.map((s) => nearestWalkable(isWalkable, s.x, s.y));
  state.pendingSummon = { spots: snapped, fireAtTick: e + STORM_TELEGRAPH_LEAD_TICKS };
  for (const s of snapped) {
    beats.push({ kind: 'telegraph', x: s.x, y: s.y, ticksUntil: STORM_TELEGRAPH_LEAD_TICKS });
  }
}

/**
 * Close the storm out - the shared ending used by both the natural end-of-clock expiry and an
 * early **0-standing wipe** (the server detects the wipe and calls this): every still-
 * living vortex plays its die clip (the server defers the despawn so the clip plays out),
 * Blackthorn speaks his parting line (his gloat on a wipe, his grumble on a survived
 * storm), and the generic end herald fires. Marks the state `ended`; the server owns the
 * mass-res, the survivor scoring, and Blackthorn's exit + despawn.
 */
export function concludeStorm(state: StormState, exitTaunt: string = STORM_EXIT_TAUNT): StormBeat[] {
  const beats: StormBeat[] = [];
  for (const id of state.vortexes) beats.push({ kind: 'vortex-die', vortex: id });
  beats.push({ kind: 'taunt', text: exitTaunt });
  beats.push({ kind: 'herald', text: STORM_END_TEXT });
  state.status = 'ended';
  return beats;
}

/**
 * Advance the storm's choreography by one server tick (PRD #289, storm fix). Drives the
 * whole tick-counted pageant off {@link StormState.elapsedTicks}:
 *
 *  - **taunt cadence** throughout, over Blackthorn's head;
 *  - **t=0** ({@link STORM_HERALD_LEAD_TICKS}): Blackthorn casts and the first vortex
 *    materializes on the **roof** beside him - the 1:30 combat clock begins;
 *  - **the scripted descent** - three quick stages, every ground summon **telegraphed
 *    ~2s early**: teleport **south of the bank** ({@link STORM_TELEPORT_TICKS}) and cast
 *    the {@link STORM_FRONT_PAIR}, then teleport **behind the bank**
 *    ({@link STORM_REAR_TELEPORT_TICKS}) and cast the {@link STORM_REAR_PAIR} - five
 *    vortexes out of the gate;
 *  - **phase 2** ({@link STORM_PHASE2_TICKS}, t+30s into the fight): Blackthorn teleports back
 *    to the **front of the bank** ({@link STORM_FRONT_ANCHOR}) and, from there, heralds
 *    and calls down {@link STORM_PHASE2_COUNT} more telegraphed vortexes at seeded spots;
 *  - **end of clock** ({@link STORM_END_TICKS}, t+1:40 from spawn): {@link concludeStorm} - every vortex dies,
 *    Blackthorn withdraws, and the server mass-resurrects, scores, and despawns.
 *
 * The server may end a storm **early** the instant nobody is left standing (a 0-standing
 * wipe) by calling {@link concludeStorm} directly - that path is not in this timeline.
 * Randomness (phase-2 spots, facings) comes only from the seeded `world.rng`, so the
 * whole timeline is deterministic under a seeded world - a test drives it by counting
 * ticks with no clock. A no-op once ended.
 */
export function stepStorm(world: World, state: StormState): StormStep {
  const beats: StormBeat[] = [];
  if (state.status !== 'active') return { beats, ended: false };

  state.elapsedTicks += 1;
  const e = state.elapsedTicks;
  // The absolute end tick derives from the storm's (possibly retuned) combat clock; the
  // scripted opening/phase-2 beats keep their fixed absolute ticks (ADR-0182).
  const endTicks = stormEndTicks(state.tuning);

  // Taunt cadence (both the pre-summon herald window and the fight itself), but never on
  // the final tick - the exit line owns that moment.
  if (e < endTicks && e - state.lastTauntTick >= STORM_TAUNT_INTERVAL_TICKS) {
    state.lastTauntTick = e;
    beats.push({ kind: 'taunt', text: STORM_TAUNTS[state.tauntSeq % STORM_TAUNTS.length]! });
    state.tauntSeq += 1;
  }

  // t=0: the first cast and the roof vortex on its fixed roof tile (not telegraphed - it is
  // his opening flourish on the roof). The 1:30 clock begins here (elapsedTicks vs END).
  if (e === STORM_HERALD_LEAD_TICKS) {
    beats.push({ kind: 'cast' });
    const spot = nearestWalkable(
      walkableForLevel(world, BANK_ROOF_Z),
      STORM_ROOF_VORTEX.x,
      STORM_ROOF_VORTEX.y,
    );
    const vortex = createVortex(world, {
      x: spot.x,
      y: spot.y,
      z: BANK_ROOF_Z,
      facing: seededFacing(world),
      // Chance this EV is a chaos llama (ADR-0162), at the storm's tuned rate (ADR-0182).
      llama: rollLlamaVariant(() => world.rng.next(), state.tuning.llamaSpawnChance),
      llamaMsPerTile: state.tuning.llamaMsPerTile,
    });
    state.vortexes.add(vortex.id);
    state.vortexCount = 1;
    beats.push({ kind: 'vortex-spawn', vortex: vortex.id });
  }

  // Stage 1: Blackthorn descends south of the bank and telegraphs the first ground pair.
  if (e === STORM_TELEPORT_TICKS) {
    teleportBlackthorn(world, state, STORM_FLOOR_ANCHOR, beats);
    armSummonWave(world, state, e, STORM_FRONT_PAIR, beats);
  }

  // Stage 2: Blackthorn flanks behind the bank and telegraphs the second ground pair.
  if (e === STORM_REAR_TELEPORT_TICKS) {
    teleportBlackthorn(world, state, STORM_REAR_ANCHOR, beats);
    armSummonWave(world, state, e, STORM_REAR_PAIR, beats);
  }

  // Phase 2 (t+30s into the fight): Blackthorn teleports back to the FRONT of the bank and, from
  // there, redoubles the storm - a herald and a fresh telegraphed host of vortexes at seeded
  // floor spots, spread across the bank.
  if (e === STORM_PHASE2_TICKS - STORM_TELEGRAPH_LEAD_TICKS) {
    teleportBlackthorn(world, state, STORM_FRONT_ANCHOR, beats);
    beats.push({ kind: 'herald', text: STORM_PHASE2_TEXT });
    const spots: { x: number; y: number }[] = [];
    for (let i = 0; i < state.tuning.phase2Count; i++) {
      spots.push(pickTelegraphSpot(world, state.region));
    }
    state.pendingSummon = { spots, fireAtTick: STORM_PHASE2_TICKS };
    for (const s of spots) {
      beats.push({ kind: 'telegraph', x: s.x, y: s.y, ticksUntil: STORM_TELEGRAPH_LEAD_TICKS });
    }
  }

  // Fire whatever wave is armed once its telegraph elapses: Blackthorn casts and every
  // telegraphed spot materializes a ground vortex at once.
  if (state.pendingSummon !== null && e === state.pendingSummon.fireAtTick) {
    beats.push({ kind: 'cast' });
    for (const spot of state.pendingSummon.spots) {
      const vortex = createVortex(world, {
        x: spot.x,
        y: spot.y,
        // Ground summon: land on the terrain (PRD #536 Slice B; flat maps: 0).
        z: world.map.groundedZ(spot.x, spot.y, 0),
        facing: seededFacing(world),
        // Independent roll per summon (ADR-0162) at the storm's tuned rate (ADR-0182).
        llama: rollLlamaVariant(() => world.rng.next(), state.tuning.llamaSpawnChance),
        llamaMsPerTile: state.tuning.llamaMsPerTile,
      });
      state.vortexes.add(vortex.id);
      state.vortexCount += 1;
      beats.push({ kind: 'vortex-spawn', vortex: vortex.id });
    }
    state.pendingSummon = null;
  }

  // end of clock: the storm passes - every vortex dies and Blackthorn withdraws.
  if (e >= endTicks) {
    beats.push(...concludeStorm(state));
    return { beats, ended: true };
  }

  return { beats, ended: false };
}

/**
 * The combat clock the {@link StormState} reports to clients: the full combat clock while the
 * storm is still heralding (the clock has not started), then counting down through the
 * combat window, clamped to zero once ended. Pure derivation of `elapsedTicks`.
 */
export function stormTicksRemaining(state: StormState): number {
  if (state.status !== 'active') return 0;
  if (state.elapsedTicks < STORM_HERALD_LEAD_TICKS) return state.tuning.combatTicks;
  return Math.max(0, stormEndTicks(state.tuning) - state.elapsedTicks);
}
